import { useRef, useEffect, useState } from "react";
import SvgComponent from "../../SVGComponent";
import SVGComponent from "../../SVGComponent";
import TickerAnimation from "../../TickerAnimation";
import { IntervalConfiguration } from "../../../../utils/configurationProvider";
import TextInputField from "../../CommonInputFields/TextInputField";
import ICONS from "../../../../assets/Icons";

/**
 * @typedef {("big" | "small")} DropdownType
 * @param {Object} props
 * @param {Array} props.options - Default options to show (string or {label, icon, longLabel})
 * @param {String} props.selected - Selected option value state
 * @param {Function} props.setSelected - State setter function for option value
 * @param {DropdownType} props.type - Dropdown type
 * @param {boolean} props.isOpen - State to open or close dropdown
 * @param {boolean} props.showSearch - State to display or hide search field
 * @param {String} props.placeholder - Default placeholder for dropdown
 * @param {String} props.searchPlaceholder - Default search placeholder
 * @param {boolean} props.disabled - Checks whether dropdown is disabled
 * @param {String} props.defaultIcon - Optional image URL to show before placeholder
 * @param {Function} props.onOpen - Callback for when dropdown is opened
 * @param {Function} props.onClose - Callback for when dropdown is closed
 *  @param {String} props.selectedIconColor - icon color for selected option
 *  @param {String} props.defaultIconColor - icon color for default option
 * @returns {JSX.Element}
 */

const SingleSelectAndInput = ({
  options,
  selected,
  setSelected,
  isOpen,
  showSearch,
  placeholder,
  searchPlaceholder,
  disabled,
  type,
  selectedIconColor,
  defaultIcon,
  defaultIconColor,
  onOpen,
  onClose,
  inputProps,
}) => {
  const singleSelectDropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionRefs = useRef([]);
  const dropdownOptionRef = useRef(null);
  const [hasSearchVal, setHasSearchVal] = useState(false);

  const [overflowFlags, setOverflowFlags] = useState([]);
  const [dropDownOptions, setDropDownOptions] = useState([]);
  const [dropdown, setDropdown] = useState(isOpen);
  const [openUpward, setOpenUpward] = useState(false);

  useEffect(() => {
    setDropDownOptions(options);
  }, [options, dropdown]);

  useEffect(() => {
    setDropdown(isOpen);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        singleSelectDropdownRef.current &&
        !singleSelectDropdownRef.current?.contains(event.target)
      ) {
        setDropdown(false);
        onClose?.call();
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [onClose]);

  const handleChange = (e) => {
    e.stopPropagation();
    setHasSearchVal(e.target.value.trim() !== "");
    let opt = options.filter((val) => {
      const label =
        (typeof val === "string" ? val : val.label) +
        " " +
        (val?.longLabel ?? "");
      return label.toLowerCase().includes(e.target.value.toLowerCase());
    });
    setDropDownOptions(opt);
  };

  const getFontSize = (type) => {
    switch (type) {
      case "small":
        return "text-font-xs";
      case "big":
        return "text-font-md";
      default:
        return "";
    }
  };

  const getMaxHeight = (type) => {
    switch (type) {
      case "small":
        return "max-h-dropdown-option-height-sm";
      case "big":
        return "max-h-dropdown-option-height-md";
      default:
        return "";
    }
  };

  useEffect(() => {
    if (dropdown) {
      const flags = dropDownOptions?.map((_, i) => {
        const el = optionRefs.current[i];
        return el?.scrollWidth > el?.offsetWidth;
      });
      setOverflowFlags(flags);
    }
  }, [dropdown, dropDownOptions]);

  return (
    <div className="w-full">
      <div
        ref={singleSelectDropdownRef}
        className={`flex flex-col relative w-full ${dropdown ? "open" : ""} ${disabled ? "cursor-not-allowed opacity-light-opacity" : "cursor-pointer opacity-base-opacity"} ${getMaxHeight(type)}`}
        onClick={() => {
          if (!disabled) {
            if (dropdown) {
              setDropdown(false);
              setTimeout(() => {
                setOpenUpward(false);
              }, IntervalConfiguration.intervalMid);
              onClose?.call();
            } else {
              const containerRect = document
                .getElementById("root")
                ?.getBoundingClientRect();
              const cardRect =
                dropdownOptionRef?.current?.getBoundingClientRect();
              // eslint-disable-next-line no-unused-vars
              const willOverflowBottom =
                cardRect?.bottom > containerRect?.bottom &&
                cardRect?.top >= cardRect?.height;

              setOpenUpward(false);
              setDropdown(true);
              onOpen?.call();
            }
          }
        }}
      >
        {/* ===== Selected Value Section ===== */}
        <div className="flex Qlg:flex-row flex-col gap-gap-sm items-center">
          <div
            className={`bg-primary-nav-act z-z-index-base relative flex items-center justify-between w-full px-padding-md py-padding-sm gap-gap-sm dropdown-header flex-[1] ${getMaxHeight(type)} ${!disabled && !dropdown && "global-hover-white"} ${dropdown ? `${openUpward ? "rounded-b-border-radius-md shadow-box-shadow-right" : "rounded-border-radius-md shadow-box-shadow-right"}` : `${openUpward ? "rounded-border-radius-md delay-[var(--animation-fast)]" : "dropdown_border-radius-md"}`}`}
          >
            <div className="flex flex-row items-center gap-gap-sm overflow-hidden">
              <>
                {!selected && defaultIcon && (
                  <div className="rounded-full bg-text-primary flex items-center justify-center">
                    <SVGComponent
                      url={defaultIcon}
                      color={defaultIconColor || "var(--text-primary)"}
                      alt="default"
                      height="var(--icon-md)"
                      width="var(--icon-md)"
                    />
                  </div>
                )}

                {/* Selected option icon */}
                {selected?.icon && (
                  <div className="rounded-full bg-text-primary flex items-center justify-center">
                    <SVGComponent
                      url={selected?.icon}
                      iconColor={selectedIconColor || "var(--text-primary)"}
                      alt={selected?.label}
                      height="var(--icon-md)"
                      width="var(--icon-md)"
                    />
                  </div>
                )}
                <span
                  className={`${getFontSize(type)} truncate select-none`}
                  style={{
                    color:
                      selected === ""
                        ? "var(--text-secondary)"
                        : "var(--text-primary)",
                  }}
                >
                  {selected?.label || placeholder}
                </span>
              </>
            </div>

            {/* ===== Right-side icons ===== */}
            <div className="flex flex-row gap-gap-sm items-center flex-shrink-0">
              <div>
                <SvgComponent
                  url={
                    dropdown ? ICONS?.ARROW_UP_WHITE : ICONS?.ARROW_DOWN_WHITE
                  }
                  color={"var(--text-primary)"}
                  height={"var(--icon-xs)"}
                  width={"var(--icon-xs)"}
                  alt="Arrow"
                />
              </div>
            </div>
          </div>
          <span className="flex-[3] w-full hidden Qsmd:inline">
            <TextInputField
              isFullWidth
              {...inputProps}
            />
          </span>
        </div>
        {/* ===== Dropdown Menu Section ===== */}
        <div className="relative mt-margin-sm">
          <div
            className={`bg-primary-nav-act absolute z-z-index-dropdown overflow-hidden w-width-full ${dropdown ? `${openUpward ? "shadow-box-shadow-right-top" : "shadow-box-shadow-right-bottom"}` : ""} ${openUpward ? `${type === "small" ? "bottom-[var(--dropdown-option-height-sm)]" : "bottom-[var(--dropdown-option-height-md)]"} flex flex-col-reverse rounded-t-border-radius-md` : "rounded-b-border-radius-md"}`}
            style={{
              borderRadius: "var(--border-radius-md)",
              maxHeight: dropdown
                ? type === "big"
                  ? "var(--max-animation-height-big-ddl)"
                  : "var(--max-animation-height-small-ddl)"
                : "0px",
              transition: "max-height var(--animation-fast) ease-in-out",
            }}
          >
            {showSearch && (
              <div className="bg-primary-nav-act px-padding-sm pt-padding-sm w-full">
                <div
                  className={`rounded-border-radius-md flex items-center px-padding-sm py-padding-sm opacity-base-opacity bg-primary-bg ${getMaxHeight(
                    type,
                  )}`}
                >
                  <div
                    className="pr-padding-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      searchInputRef.current?.focus();
                    }}
                  >
                    <SVGComponent
                      url={ICONS?.SEARCH_WHITE}
                      alt="search"
                      width="var(--icon-md)"
                      height="var(--icon-md)"
                      color="var(--text-primary)"
                    />
                  </div>

                  <input
                    tabIndex={dropdown ? 0 : -1}
                    className={`w-full outline-none bg-transparent text-left font-normal ml-margin-sm  text-text-secondary border-text-primary placeholder-text-secondary ${getFontSize(type)}`}
                    ref={searchInputRef}
                    type="text"
                    onChange={handleChange}
                    onClick={(e) => e.stopPropagation()}
                    placeholder={searchPlaceholder}
                  />
                  {hasSearchVal && (
                    <img
                      src={ICONS?.CLOSE}
                      alt="clear"
                      className="h-icon-md w-icon-md"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (searchInputRef.current) {
                          searchInputRef.current.value = "";
                        }
                        setHasSearchVal(false);
                        setDropDownOptions(options);
                      }}
                      style={{
                        cursor: "pointer",
                      }}
                    />
                  )}
                </div>
              </div>
            )}

            <div
              ref={dropdownOptionRef}
              className={`flex flex-col pt-padding-sm pl-padding-sm pb-padding-base bg-primary-nav-act w-full rounded-b-border-radius-md overflow-auto border-t-border-base cursor-pointer gap-gap-sm ${type === "big" ? "max-h-max-overflow-height-big-ddl-options" : "max-h-max-overflow-height-small-ddl-options"}`}
            >
              <div className="flex flex-col pr-padding-sm bg-primary-nav-act w-full overflow-auto gap-gap-sm mb-margin-sm">
                {dropDownOptions?.map((opt, index) => {
                  return (
                    <div
                      key={index}
                      title={overflowFlags[index] ? opt.label : ""}
                      className={`relative max-w-full gap-gap-sm z-z-index-base global-hover-white py-padding-sm px-padding-sm truncate text-text-primary flex justify-between items-center rounded-border-radius-md flex-shrink-0 ${getMaxHeight(type)} ${getFontSize(type)} ${selected?.label === opt.label ? "bg-primary-nav" : ""}`}
                      onClick={() => setSelected(opt)}
                    >
                      <div className="flex flex-row items-center gap-gap-sm overflow-hidden">
                        <div className="flex rounded-full bg-text-primary">
                          <img
                            src={opt.icon}
                            alt={opt?.label}
                            className="flex-shrink-0 h-icon-md w-icon-md flex items-center justify-center"
                          />
                        </div>

                        <p
                          ref={(el) => (optionRefs.current[index] = el)}
                          className="truncate"
                        >
                          <TickerAnimation
                            text={opt?.longLabel || opt?.label}
                          />
                        </p>
                      </div>
                      {selected?.label === opt?.label && (
                        <SvgComponent
                          url={ICONS?.CHECK_MARK_WHITE}
                          color={"var(--text-primary)"}
                          height={"var(--icon-sm)"}
                          width={"var(--icon-sm)"}
                          alt="Checkmark"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <span className="flex-[3] w-full Qsmd:hidden inline">
          <TextInputField
            isFullWidth
            {...inputProps}
          />
        </span>
      </div>
    </div>
  );
};

export default SingleSelectAndInput;
